<template>
	<div
		class="slider"
		aria-disabled="false"
		@mousedown="initMouseDrag"
		@touchstart="initTouchDrag"
		@touchmove="doTouchDrag"
		@touchend="stopTouchDrag"
		ref="slider"
	>
		<template v-if="showBar">
			<div
				v-if="! (isRange && reverseBar)"
				class="ap-fill-y slider-bar"
				:style="barStyle"
			/>
			<template v-else>
				<div
					class="ap-fill-y slider-bar"
					:style="barLeftStyle"
				/>
				<div
					class="ap-fill-y slider-bar"
					:style="barRightStyle"
				/>
			</template>
		</template>
		<div
			v-if="isRange"
			class="slider-handle slider-handle-left"
			ref="leftHandle"
			:style="styleHandleLeft"
		/>
		<div
			class="slider-handle slider-handle-right"
			ref="rightHandle"
			:style="styleHandleRight"
		/>
	</div>
</template>

<script>
import  { clamp, isEqual, throttle } from 'lodash';
import { toPrecision } from 'utils';
import SliderMapping, { linear } from 'sliderMapping';

export default {
	data () {
		const internalValue = {
			left: this.leftCap,
			right: this.rightCap,
		};
		if (this.value instanceof Object) {
			internalValue.left = this.value.left;
			internalValue.right = this.value.left;
		} else {
			internalValue.right = this.value;
		}
		return {
			internalValue,
			recalculate: 0,
			currentMouseDrag: null,
			currentTouchDrag: null,
		};
	},
	props: {
		leftCap: {
			type: Number,
			default: 0,
		},
		rightCap: {
			type: Number,
			default: 100,
		},
		value: {
			type: [Number, Object, String],
			required: true,
		},
		precision: {
			type: Number,
		},
		mapping: {
			type: SliderMapping,
			default: () => linear,
		},
		showBar: {
			type: Boolean,
			default: true,
		},
		reverseBar: {
			type: Boolean,
			default: false,
		},
		/**
		 * Determines the minimum possible value that is added/subtracted when the
		 * user drags the slider.
		 */
		step: {
			type: Number,
			default: 1,
		},
	},
	computed: {
		sliderLeftPosition: {
			get () {
				const pos = this.handleLeftWidth / 2 + this.sliderPixelRange * this.mapping.toSlider((this.internalValue.left - this.leftCap) / this.valueRange);
				return pos;
			},
			set (val) {
				val -= this.handleLeftWidth / 2;
				val = this.leftCap + this.mapping.fromSlider(val / this.sliderPixelRange) * this.valueRange;
				if (this.step !== 0 && val < this.rightCap) {
					val = Math.round(val / this.step) * this.step;
				}
				this.internalValue.left = clamp(this.precision !== undefined ? toPrecision(val, this.precision) : val, this.leftCap, this.internalValue.right);
			},
		},
		sliderRightPosition: {
			get () {
				const pos = this.handleRightWidth / 2 + this.sliderPixelRange * (1 - this.mapping.toSlider((this.internalValue.right - this.leftCap) / this.valueRange));
				return pos;
			},
			set (val) {
				val += this.handleRightWidth;
				val = this.leftCap + this.mapping.fromSlider((this.sliderPixelRange - val) / this.sliderPixelRange) * this.valueRange;
				if (this.step !== 0 && val < this.rightCap) {
					val = Math.round(val / this.step) * this.step;
				}
				this.internalValue.right = clamp(this.precision !== undefined ? toPrecision(val, this.precision) : val, this.internalValue.left, this.rightCap);
			},
		},
		isRange () {
			return this.value instanceof Object;
		},
		handleLeftWidth () {
			this.recalculate;
			const handle = this.$refs.leftHandle;
			return handle ? handle.offsetWidth : 0;
		},
		handleRightWidth () {
			this.recalculate;
			const handle = this.$refs.rightHandle;
			return handle ? handle.offsetWidth : 0;
		},
		sliderWidth () {
			this.recalculate;
			const slider = this.$refs.slider;
			return slider ? slider.offsetWidth : 0;
		},
		sliderOffset () {
			return this.handleRightWidth + (this.isRange ? this.handleLeftWidth : 0);
		},
		sliderPixelRange () {
			return this.sliderWidth - this.sliderOffset;
		},
		valueRange () {
			return this.rightCap - this.leftCap;
		},
		barStyle () {
			let style;
			if (! this.isRange) {
				if (this.reverseBar) {
					style = {
						left: this.sliderWidth + 'px',
					};
				} else {
					style = {
						right: this.sliderRightPosition + 'px',
					};
				}
			} else {
				style = {
					left: this.sliderLeftPosition + 'px',
					right: this.sliderRightPosition + 'px',
				};
			}
			return style;
		},
		barLeftStyle () {
			const style = {
				right: this.sliderWidth - this.sliderLeftPosition + 'px',
			};
			return style;
		},
		barRightStyle () {
			return {
				left: this.sliderWidth - this.sliderRightPosition + 'px',
			};
		},
		styleHandleLeft () {
			return {
				left: this.sliderLeftPosition + 'px',
			};
		},
		styleHandleRight () {
			return {
				right: this.sliderRightPosition + 'px',
			};
		},
	},
	methods: {
		updateInternalValue () {
			const val = this.value;
			if (! isEqual(val, this.internalValue)) {
				if (val instanceof Object) {
					this.internalValue.left = val.left;
					this.internalValue.right = val.right;
				} else {
					this.internalValue.right = val;
				}
			}
		},
		doMouseDrag (e) {
			if (! (e.buttons & 1)) {
				this.stopMouseDrag();
			}
			const x = e.x - this.$refs.slider.getBoundingClientRect().left;
			switch (this.currentMouseDrag) {
				case 0:
					this.doDragLeft(x);
					break;
				case 1:
					this.doDragRight(x);
					break;
			}
		},
		doTouchDrag (e) {
			const x = e.touches[0].clientX - this.$refs.slider.getBoundingClientRect().left;
			e.preventDefault();
			switch (this.currentTouchDrag) {
				case 0:
					this.doDragLeft(x);
					break;
				case 1:
					this.doDragRight(x);
					break;
			}
		},
		doDragLeft: throttle(function (x) {
			this.sliderLeftPosition = x;
			this.emit('input');
		}, 20),
		doDragRight: throttle(function (x) {
			this.sliderRightPosition = this.sliderPixelRange - x;
			this.emit('input');
		}, 20),
		initMouseDrag (event) {
			if (event.button !== 0) return;
			this.stopTouchDrag(false);
			const x = event.x - this.$refs.slider.getBoundingClientRect().left;
			const normalizedLeft = this.sliderLeftPosition;
			const normalizedRight = this.sliderWidth - this.sliderRightPosition;
			const center = (normalizedLeft * 2 + normalizedRight) * .3333;
			if (x < center) {
				this.currentMouseDrag = 0;
				this.doDragLeft(x);
			} else {
				this.currentMouseDrag = 1;
				this.doDragRight(x);
			}

			event.preventDefault();
			window.addEventListener('mousemove', this.doMouseDrag);
			window.addEventListener('mouseup', this.stopMouseDrag);
			this.emit('input');
		},
		initTouchDrag (event) {
			event.preventDefault();
			this.stopMouseDrag(false);
			const x = event.touches[0].clientX - this.$refs.slider.getBoundingClientRect().left;
			const normalizedLeft = this.sliderLeftPosition;
			const normalizedRight = this.sliderWidth - this.sliderRightPosition;
			const center = (normalizedLeft * 3 + normalizedRight) * .25;
			if (x < center) {
				this.currentTouchDrag = 0;
				this.doDragLeft(x);
			} else {
				this.currentTouchDrag = 1;
				this.doDragRight(x);
			}

			event.preventDefault();
			this.emit('input');
		},
		stopMouseDrag (emit = true) {
			this.currentMouseDrag = null;
			window.removeEventListener('mousemove', this.doMouseDrag);
			window.removeEventListener('mouseup', this.stopMouseDrag);
			if (emit) this.emit('change');
		},
		stopTouchDrag (emit = true) {
			this.currentTouchDrag = null;
			if (emit) this.emit('change');
		},
		emit (event) {
			this.$emit(event, this.isRange ? Object.assign({}, this.internalValue) : this.internalValue.right);
		},
		/** Sometimes the offset values are set before the DOM is properly rendered,
		 * so this checks that the offsetWidth isn't 0, and if it is, it checks until it is
		 * larger than 0 and then recalculates the proper values for slider elements
		*/
		waitForDOMToLoad () {
			if (! this.$refs.slider.offsetWidth) {
				setTimeout(() => {
					this.waitForDOMToLoad();
				}, 10);
			} else {
				this.recalculate++;
			}
		},
	},
	mounted () {
		this.waitForDOMToLoad();
		this.updateInternalValue();
	},
	watch: {
		value: {
			handler (to) {
				this.updateInternalValue();
			},
			deep: true,
		},
	},
};

</script>
